import {FC, useEffect, useMemo, useState} from 'react'
import {api, Payment, AuthUser, PaymentStatusValue, PaymentMethodValue, ApiError, RecordPaymentInput} from '../../services/api'
import {Pagination} from '../../components/Pagination'
import {Spinner} from '../../components/Spinner'
import {useToast} from '../../components/Toast'
import {confirmDialog} from '../../components/dialog'
import {PageHead} from '../../components/PageHead'
import {ListToolbar, FilterSelect} from '../../components/ListToolbar'
import {ModalPortal} from '../../components/ModalPortal'
import {useIsMobile} from '../../hooks/useIsMobile'

const ACCENT = '#039540'
const INK = '#1b1b29'
const MUTED = '#7e8299'
const PAGE_SIZE = 10
const METHODS: PaymentMethodValue[] = ['ZELLE', 'CASH', 'CARD', 'OTHER']

const STATUS_TONE: Record<PaymentStatusValue, {color: string; bg: string}> = {
  PAID: {color: '#1f9d5b', bg: 'rgba(3,149,64,0.12)'},
  PENDING: {color: '#b8770a', bg: 'rgba(255,168,0,0.15)'},
  OVERDUE: {color: '#d9214e', bg: 'rgba(217,33,78,0.1)'},
  REFUNDED: {color: '#7e8299', bg: 'rgba(126,130,153,0.15)'},
}
const Pill: FC<{status: PaymentStatusValue}> = ({status}) => {
  const t = STATUS_TONE[status]
  return <span style={{fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', borderRadius: 999, padding: '4px 10px', color: t.color, background: t.bg}}>{status}</span>
}

const PaymentsPage: FC = () => {
  const [rows, setRows] = useState<Payment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [methodFilter, setMethodFilter] = useState('')
  const [page, setPage] = useState(1)
  const [recording, setRecording] = useState(false)
  const [detailsFor, setDetailsFor] = useState<Payment | null>(null)
  const [busy, setBusy] = useState<{id: string; kind: 'receipt' | 'refund'} | null>(null)
  const toast = useToast()
  const isMobile = useIsMobile()

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      setRows(await api.listPayments())
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load payments')
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    void load()
  }, [])
  useEffect(() => setPage(1), [search, statusFilter, methodFilter])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter((p) => {
      if (q && ![p.renter?.firstName, p.renter?.lastName, p.renter?.email, p.receiptNumber, p.bed?.code].filter(Boolean).join(' ').toLowerCase().includes(q)) return false
      if (statusFilter && p.status !== statusFilter) return false
      if (methodFilter && p.method !== methodFilter) return false
      return true
    })
  }, [rows, search, statusFilter, methodFilter])
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  useEffect(() => {
    if (page > pageCount) setPage(pageCount)
  }, [page, pageCount])
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const onRecorded = (p: Payment) => {
    setRows((list) => [p, ...list])
    setRecording(false)
    toast.success(p.status === 'PAID' ? `Payment recorded — receipt ${p.receiptNumber} emailed.` : `Payment ${p.receiptNumber} recorded.`)
  }

  const resend = async (p: Payment) => {
    setBusy({id: p.id, kind: 'receipt'})
    try {
      await api.resendReceipt(p.id)
      toast.success(`Receipt ${p.receiptNumber} emailed to ${p.renter?.email ?? 'the renter'}.`)
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not send receipt')
    } finally {
      setBusy(null)
    }
  }
  const refund = async (p: Payment) => {
    const ok = await confirmDialog({title: 'Refund payment?', text: `Mark ${p.receiptNumber} ($${p.amount}) as refunded?`, confirmText: 'Refund', danger: true})
    if (!ok) return
    setBusy({id: p.id, kind: 'refund'})
    try {
      const u = await api.updatePayment(p.id, {status: 'REFUNDED'})
      setRows((list) => list.map((x) => (x.id === p.id ? u : x)))
      toast.success(`${p.receiptNumber} marked refunded.`)
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not update payment')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div>
      <PageHead
        icon='💳'
        eyebrow='Operations'
        title='Payments'
        sub='Record payments and issue receipts.'
        action={
          <button onClick={() => setRecording(true)} className='ccp-cta-light' style={{fontWeight: 700, fontSize: 14, color: ACCENT, background: '#fff', border: 'none', borderRadius: 10, padding: '10px 18px', cursor: 'pointer'}}>Record payment</button>
        }
      />

      <ListToolbar search={search} onSearch={setSearch} placeholder='Search payments…'>
        <FilterSelect
          value={statusFilter}
          onChange={setStatusFilter}
          ariaLabel='Filter by status'
          options={[{label: 'All statuses', value: ''}, {label: 'Paid', value: 'PAID'}, {label: 'Pending', value: 'PENDING'}, {label: 'Overdue', value: 'OVERDUE'}, {label: 'Refunded', value: 'REFUNDED'}]}
        />
        <FilterSelect
          value={methodFilter}
          onChange={setMethodFilter}
          ariaLabel='Filter by method'
          options={[{label: 'All methods', value: ''}, {label: 'Zelle', value: 'ZELLE'}, {label: 'Cash', value: 'CASH'}, {label: 'Card', value: 'CARD'}, {label: 'Other', value: 'OTHER'}]}
        />
      </ListToolbar>

      <div className='card ccp-fade-up-2' style={{borderRadius: 16, border: 'none', boxShadow: '0 6px 18px rgba(27,27,41,0.06)'}}>
        <div className='card-body p-0'>
          {loading ? (
            <div style={{padding: 40, textAlign: 'center', color: MUTED}}>Loading…</div>
          ) : error ? (
            <div style={{padding: 40, textAlign: 'center', color: '#d9214e'}}>{error}</div>
          ) : filtered.length === 0 ? (
            <div style={{padding: 40, textAlign: 'center', color: MUTED}}>{rows.length === 0 ? 'No payments recorded yet.' : 'No matches.'}</div>
          ) : (
            <>
              {isMobile ? (
                <div>
                  {paged.map((p) => (
                    <div key={p.id} style={{borderTop: '1px solid #f1f3f7', padding: '14px 16px'}}>
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10}}>
                        <div style={{minWidth: 0}}>
                          <div style={{fontWeight: 700, color: INK}}>{p.receiptNumber}</div>
                          <div style={{fontWeight: 600, color: INK, marginTop: 2}}>{p.renter?.firstName} {p.renter?.lastName}</div>
                          <div style={{color: MUTED, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{p.renter?.email}</div>
                        </div>
                        <Pill status={p.status} />
                      </div>
                      <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px 16px', marginTop: 8, fontSize: 13, color: MUTED}}>
                        <span style={{fontWeight: 700, color: INK}}>${p.amount}</span>
                        <span>{p.method}</span>
                        <span>Bed {p.bed?.code ?? '—'}</span>
                        <span>{p.paidAt ?? new Date(p.createdAt).toLocaleDateString()}</span>
                      </div>
                      <div style={{display: 'flex', gap: 8, marginTop: 12}}>
                        <button onClick={() => setDetailsFor(p)} style={{...btnGhost, flex: 1}}>Details</button>
                        <button onClick={() => resend(p)} disabled={busy?.id === p.id} style={{...btnGhost, flex: 1}}>
                          {busy?.id === p.id && busy.kind === 'receipt' ? <Spinner size={13} /> : 'Receipt'}
                        </button>
                        {p.status !== 'REFUNDED' && (
                          <button onClick={() => refund(p)} disabled={busy?.id === p.id} style={{...btnDanger, flex: 1}}>
                            {busy?.id === p.id && busy.kind === 'refund' ? <Spinner size={13} /> : 'Refund'}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{overflowX: 'auto'}}>
                  <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14}}>
                    <thead>
                      <tr style={{textAlign: 'left', color: MUTED, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em'}}>
                        <th style={th}>Receipt</th>
                        <th style={th}>Renter</th>
                        <th style={th}>Bed</th>
                        <th style={th}>Amount</th>
                        <th style={th}>Method</th>
                        <th style={th}>Status</th>
                        <th style={th}>Date</th>
                        <th style={{...th, textAlign: 'right'}}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paged.map((p) => (
                        <tr key={p.id} style={{borderTop: '1px solid #f1f3f7'}}>
                          <td style={{...td, fontWeight: 600, color: INK}}>{p.receiptNumber}</td>
                          <td style={td}>
                            <div style={{fontWeight: 600, color: INK}}>{p.renter?.firstName} {p.renter?.lastName}</div>
                            <div style={{color: MUTED, fontSize: 13}}>{p.renter?.email}</div>
                          </td>
                          <td style={{...td, color: MUTED}}>{p.bed?.code ?? '—'}</td>
                          <td style={{...td, fontWeight: 700, color: INK}}>${p.amount}</td>
                          <td style={{...td, color: MUTED}}>{p.method}</td>
                          <td style={td}><Pill status={p.status} /></td>
                          <td style={{...td, color: MUTED}}>{p.paidAt ?? new Date(p.createdAt).toLocaleDateString()}</td>
                          <td style={{...td, textAlign: 'right', whiteSpace: 'nowrap'}}>
                            <button onClick={() => setDetailsFor(p)} style={{...btnGhost, minWidth: 70}}>Details</button>
                            <button onClick={() => resend(p)} disabled={busy?.id === p.id} style={{...btnGhost, minWidth: 78}}>
                              {busy?.id === p.id && busy.kind === 'receipt' ? <Spinner size={13} /> : 'Receipt'}
                            </button>
                            {p.status !== 'REFUNDED' && (
                              <button onClick={() => refund(p)} disabled={busy?.id === p.id} style={{...btnDanger, minWidth: 72}}>
                                {busy?.id === p.id && busy.kind === 'refund' ? <Spinner size={13} /> : 'Refund'}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div style={{borderTop: '1px solid #f1f3f7'}}>
                <Pagination page={page} total={filtered.length} pageSize={PAGE_SIZE} onChange={setPage} />
              </div>
            </>
          )}
        </div>
      </div>

      {recording && <ModalPortal><RecordModal onClose={() => setRecording(false)} onRecorded={onRecorded} /></ModalPortal>}
      {detailsFor && <ModalPortal><PaymentDetailsModal payment={detailsFor} onClose={() => setDetailsFor(null)} /></ModalPortal>}
    </div>
  )
}

/** Parse the deposit-refund note into a structured breakdown (net + deductions). */
function parseRefund(note: string | null): {refunded: string; original: string; deductions: {amount: string; reason: string}[]} | null {
  if (!note) return null
  const m = note.match(/Refunded \$(\d+(?:\.\d+)?) of \$(\d+(?:\.\d+)?)/)
  if (!m) return null
  const deductions: {amount: string; reason: string}[] = []
  const w = note.match(/Withheld:\s*(.+?)\.?\s*$/)
  if (w) {
    for (const part of w[1].split(';')) {
      const dm = part.trim().match(/\$?(\d+(?:\.\d+)?)\s*[—–-]\s*(.+)/)
      if (dm) deductions.push({amount: dm[1], reason: dm[2].trim()})
    }
  }
  return {refunded: m[1], original: m[2], deductions}
}

const PaymentDetailsModal: FC<{payment: Payment; onClose: () => void}> = ({payment: p, onClose}) => {
  const refund = p.status === 'REFUNDED' ? parseRefund(p.note) : null
  const totalDeduct = refund ? refund.deductions.reduce((s, d) => s + Number(d.amount), 0) : 0
  return (
    <div onClick={onClose} style={{position: 'fixed', inset: 0, background: 'rgba(8,10,14,0.5)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '50px 20px', overflowY: 'auto'}}>
      <div onClick={(e) => e.stopPropagation()} style={{background: '#fff', borderRadius: 16, padding: '26px 24px', width: '100%', maxWidth: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.3)'}}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16}}>
          <div>
            <h2 style={{fontSize: 20, fontWeight: 800, color: INK, margin: '0 0 2px'}}>{p.receiptNumber}</h2>
            <div style={{color: MUTED, fontSize: 14}}>{p.renter?.firstName} {p.renter?.lastName} · Bed {p.bed?.code ?? '—'}</div>
          </div>
          <Pill status={p.status} />
        </div>

        <div style={{fontSize: 14}}>
          <DetailRow label='Amount' value={`$${p.amount}`} strong />
          <DetailRow label='Method' value={p.method} />
          {p.periodLabel && <DetailRow label='For' value={p.periodLabel} />}
          {p.reference && <DetailRow label='Reference' value={p.reference} />}
          <DetailRow label='Date' value={p.paidAt ?? new Date(p.createdAt).toLocaleDateString()} />
        </div>

        {refund ? (
          <div style={{marginTop: 16, background: '#f8fafc', border: '1px solid #eef2f7', borderRadius: 10, padding: '14px 16px', fontSize: 14}}>
            <div style={{fontSize: 12, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8}}>Refund breakdown</div>
            <DetailRow label='Deposit held' value={`$${Number(refund.original).toFixed(2)}`} />
            {refund.deductions.length > 0 && (
              <>
                <div style={{margin: '6px 0 2px', color: MUTED, fontSize: 12.5}}>Deductions</div>
                {refund.deductions.map((d, i) => (
                  <div key={i} style={{display: 'flex', justifyContent: 'space-between', padding: '2px 0'}}>
                    <span style={{color: '#334155'}}>{d.reason}</span>
                    <span style={{color: '#d9214e', fontWeight: 600}}>− ${Number(d.amount).toFixed(2)}</span>
                  </div>
                ))}
                <div style={{borderTop: '1px dashed #dbe2ea', margin: '8px 0'}} />
              </>
            )}
            <div style={{display: 'flex', justifyContent: 'space-between'}}>
              <span style={{fontWeight: 700, color: INK}}>Refunded to renter</span>
              <span style={{fontWeight: 800, color: ACCENT}}>${Number(refund.refunded).toFixed(2)}</span>
            </div>
            {totalDeduct > 0 && <div style={{fontSize: 12, color: MUTED, marginTop: 6}}>Owner retained ${totalDeduct.toFixed(2)} in expenses.</div>}
          </div>
        ) : p.note ? (
          <div style={{marginTop: 16, background: '#f8fafc', border: '1px solid #eef2f7', borderRadius: 10, padding: '12px 14px', fontSize: 13.5, color: '#334155', lineHeight: 1.5}}>
            <div style={{fontSize: 12, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6}}>Note</div>
            {p.note}
          </div>
        ) : null}

        {p.stripeResponse && (
          <details style={{marginTop: 16, background: '#0f172a', borderRadius: 10, overflow: 'hidden'}}>
            <summary style={{cursor: 'pointer', listStyle: 'none', padding: '12px 14px', fontSize: 12, fontWeight: 700, color: '#cbd5e1', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <span>Stripe gateway response</span>
              <span style={{fontSize: 11, color: '#64748b', textTransform: 'none', letterSpacing: 0}}>tap to expand</span>
            </summary>
            <pre style={{margin: 0, padding: '0 14px 14px', fontSize: 11.5, lineHeight: 1.55, color: '#93c5fd', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', whiteSpace: 'pre', overflowX: 'auto', maxHeight: 320, overflowY: 'auto'}}>
              {JSON.stringify(p.stripeResponse, null, 2)}
            </pre>
          </details>
        )}

        <button onClick={onClose} style={{width: '100%', marginTop: 18, ...btnGhost, padding: '11px 0', background: ACCENT, color: '#fff', border: 'none', fontWeight: 700}}>Close</button>
      </div>
    </div>
  )
}

const DetailRow: FC<{label: string; value: string; strong?: boolean}> = ({label, value, strong}) => (
  <div style={{display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, padding: '3px 0'}}>
    <span style={{color: MUTED, flexShrink: 0}}>{label}</span>
    <span style={{fontWeight: strong ? 800 : 600, color: INK, textAlign: 'right', minWidth: 0, wordBreak: 'break-all', overflowWrap: 'break-word'}}>{value}</span>
  </div>
)

const RecordModal: FC<{onClose: () => void; onRecorded: (p: Payment) => void}> = ({onClose, onRecorded}) => {
  const [renters, setRenters] = useState<AuthUser[]>([])
  const [form, setForm] = useState<RecordPaymentInput>({userId: '', amount: '', method: 'ZELLE', status: 'PAID'})
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    api.listUsers('RENTER').then(setRenters).catch(() => setRenters([]))
  }, [])

  const set = (patch: Partial<RecordPaymentInput>) => setForm((f) => ({...f, ...patch}))

  const save = async () => {
    setErr(null)
    if (!form.userId) return setErr('Choose a renter.')
    if (!/^\d+(\.\d{1,2})?$/.test(form.amount)) return setErr('Enter a valid amount (e.g. 250.00).')
    setSaving(true)
    try {
      onRecorded(await api.recordPayment({...form, periodLabel: form.periodLabel || undefined, reference: form.reference || undefined, note: form.note || undefined}))
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not record')
      setSaving(false)
    }
  }

  return (
    <div onClick={onClose} style={{position: 'fixed', inset: 0, background: 'rgba(8,10,14,0.5)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '50px 20px', overflowY: 'auto'}}>
      <div onClick={(e) => e.stopPropagation()} style={{background: '#fff', borderRadius: 16, padding: '26px 24px', width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.3)'}}>
        <h2 style={{fontSize: 20, fontWeight: 800, color: INK, margin: '0 0 18px'}}>Record payment</h2>
        {err && <div style={{background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, padding: '10px 14px', fontSize: 14, marginBottom: 14}}>{err}</div>}
        <div className='row g-3'>
          <Field label='Renter' full>
            <select value={form.userId} onChange={(e) => set({userId: e.target.value})} style={inp}>
              <option value=''>Select a renter…</option>
              {renters.map((r) => <option key={r.id} value={r.id}>{r.firstName} {r.lastName} — {r.email}</option>)}
            </select>
          </Field>
          <Field label='Amount ($)'>
            <input value={form.amount} onChange={(e) => set({amount: e.target.value})} placeholder='250.00' style={inp} />
          </Field>
          <Field label='Method'>
            <select value={form.method} onChange={(e) => set({method: e.target.value as PaymentMethodValue})} style={inp}>
              {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </Field>
          <Field label='Status'>
            <select value={form.status} onChange={(e) => set({status: e.target.value as PaymentStatusValue})} style={inp}>
              <option value='PAID'>PAID</option>
              <option value='PENDING'>PENDING</option>
              <option value='OVERDUE'>OVERDUE</option>
            </select>
          </Field>
          <Field label='Period (optional)'>
            <input value={form.periodLabel ?? ''} onChange={(e) => set({periodLabel: e.target.value})} placeholder='July 2026' style={inp} />
          </Field>
          <Field label='Reference (optional)' full>
            <input value={form.reference ?? ''} onChange={(e) => set({reference: e.target.value})} placeholder='Zelle confirmation / memo' style={inp} />
          </Field>
        </div>
        <p style={{color: MUTED, fontSize: 13, margin: '14px 0 0'}}>A PAID payment emails the renter a receipt.</p>
        <div style={{display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18}}>
          <button onClick={onClose} style={btnGhost}>Cancel</button>
          <button onClick={save} disabled={saving} style={{...btnGhost, background: ACCENT, color: '#fff', border: 'none', minWidth: 170}}>{saving ? <Spinner size={14} /> : 'Record & send receipt'}</button>
        </div>
      </div>
    </div>
  )
}

const Field: FC<{label: string; full?: boolean; children: React.ReactNode}> = ({label, full, children}) => (
  <div className={full ? 'col-12' : 'col-6'}>
    <label style={{display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6}}>{label}</label>
    {children}
  </div>
)

const inp: React.CSSProperties = {width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none', background: '#fff'}
const th: React.CSSProperties = {padding: '13px 16px', fontWeight: 600}
const td: React.CSSProperties = {padding: '13px 16px', verticalAlign: 'middle'}
const btnBase: React.CSSProperties = {fontSize: 13, fontWeight: 600, borderRadius: 8, padding: '7px 13px', cursor: 'pointer', marginLeft: 8, border: '1px solid transparent'}
const btnGhost: React.CSSProperties = {...btnBase, marginLeft: 0, background: '#fff', border: '1px solid #e5e7eb', color: INK}
const btnDanger: React.CSSProperties = {...btnBase, background: 'rgba(217,33,78,0.1)', color: '#d9214e'}

export {PaymentsPage}
